import { Dispatch } from "react";
import classNames from "classnames";
import { MixPuzzleInstrument } from "shared/games/mixpuzzles/types";
import prettyInstrument from "util/formatters/prettyInstrument";
import Button from "ui/atoms/Button/Button";
import { motion } from "framer-motion";
import { State } from "../../state/reducer";
import Action from "../../state/actions";
import {
  isInstrumentTechniqueCorrect,
  isInstrumentCorrect,
} from "../../state/selectors";
import TechniqueAnswerChip from "./atoms/TechniqueAnswerChip";
import { difficultySettings } from "../../constants";

interface Props {
  instrument: MixPuzzleInstrument;
  active: boolean | null;
  state: State;
  afterSelectedCallback?: () => void;
  dispatch: Dispatch<Action>;
}

export default function ChannelReportCardStrip(props: Props) {
  const { instrument, active, state, dispatch } = props;
  const correct = isInstrumentCorrect(state, instrument.instrument);
  const compressionEnabled = difficultySettings.compression(state);

  const onSelectInstrument = () => {
    props.afterSelectedCallback?.();

    dispatch({
      type: "updateSelectedInstrument",
      payload: instrument.instrument,
    });
  };

  const borderClassName = classNames({
    "border-green-500": correct,
    "border-red-500": !correct,
  });

  const bgClassName = classNames({
    "bg-green-500": correct,
    "bg-red-500": !correct,
  });

  return (
    <div
      onClick={onSelectInstrument}
      className={classNames(
        "relative z-0 flex cursor-pointer rounded border-4",
        borderClassName
      )}
    >
      {active && (
        <motion.div
          initial={{ x: 0, y: "-50%" }}
          animate={{ x: "100%", y: "-50%" }}
          exit={{ x: 0, y: "-50%" }}
          className={classNames(
            "absolute right-0 top-1/2 -z-10 h-10 w-5",
            bgClassName
          )}
        />
      )}

      <div className="w-full bg-black">
        {/* title */}
        <div
          className={
            "flex items-center justify-between overflow-auto px-2 py-2"
          }
        >
          <div>
            <div className={"font-brik leading-none"}>
              {prettyInstrument(instrument.instrument)}
            </div>
            <div className={"text-xs"}>
              This track has been mixed{" "}
              {correct ? (
                <span className={"font-bold text-green-500"}>correctly</span>
              ) : (
                <span className={"font-bold text-red-500"}>incorrectly</span>
              )}
              .
            </div>
          </div>

          <div className={"flex"}>
            <Button
              theme="default"
              active={active === null ? undefined : active}
              onClick={onSelectInstrument}
            >
              View →
            </Button>
          </div>
        </div>

        <hr className={classNames("border-2", borderClassName)} />

        <div className={"pl-2 pt-2"}>
          <TechniqueAnswerChip
            text={"Volume"}
            correct={isInstrumentTechniqueCorrect(
              state,
              instrument.instrument,
              "volume"
            )}
          />

          <TechniqueAnswerChip
            text={"Pan"}
            correct={isInstrumentTechniqueCorrect(
              state,
              instrument.instrument,
              "pan"
            )}
          />

          <TechniqueAnswerChip
            text={"Equalization"}
            correct={isInstrumentTechniqueCorrect(
              state,
              instrument.instrument,
              "eq"
            )}
          />

          {compressionEnabled && (
            <TechniqueAnswerChip
              text={"Compression"}
              correct={isInstrumentTechniqueCorrect(
                state,
                instrument.instrument,
                "comp"
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
